import {
  DNS_NAME_PATTERN,
  type IpFamily,
  type PublicIp,
  type ReverseDnsRequest,
  type Server,
} from "@cloudserver/contracts";
import {
  Globe,
  Link2,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Unlink,
  Waypoints,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useCanWrite, useReadOnly } from "../api/users.js";
import { ApiProblem } from "../api/client.js";
import {
  useAssignPublicIp,
  useCreatePublicIp,
  useDeletePublicIp,
  useIpProtection,
  usePublicIps,
  useReverseDns,
  useUnassignPublicIp,
  useUpdatePublicIp,
} from "../api/public-ips.js";
import { useCatalogue } from "../api/queries.js";
import { useServers } from "../api/servers.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError, problemText } from "../ui/feedback.js";
import { money, relativeTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog, type MenuItem } from "../ui/overlay.js";
import { Alert, Badge, Button, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";

type T = ReturnType<typeof useTranslation>["t"];

/** Why a server cannot take this IP now, or null when it can. */
function serverBlocker(t: T, server: Server, family: IpFamily, region?: string): string | null {
  if (region !== undefined && server.region !== region) return t("publicIps.why.otherRegion");
  if ((family === "ipv4" ? server.publicIpv4 : server.publicIpv6) !== null)
    return t("publicIps.why.hasAddress", { family: family === "ipv4" ? "IPv4" : "IPv6" });
  if (server.status !== "off") return t("publicIps.why.running");
  return null;
}

/** Server choice as a native select; servers that cannot take the IP stay visible with the reason. */
function ServerSelect({
  servers,
  family,
  region,
  value,
  onChange,
}: {
  servers: Server[];
  family: IpFamily;
  region?: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium">{t("publicIps.server")}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-10 rounded-lg border border-line bg-surface px-3 text-sm"
        required
      >
        <option value="">{t("publicIps.chooseServer")}</option>
        {servers.map((s) => {
          const why = serverBlocker(t, s, family, region);
          return (
            <option key={s.id} value={s.id} disabled={why !== null}>
              {s.name} ({s.region}){why ? ` — ${why}` : ""}
            </option>
          );
        })}
      </select>
      <span className="text-xs text-muted">{t("publicIps.offOnly")}</span>
    </label>
  );
}

export function FamilyChoice({
  family,
  onChange,
  ipv4Price,
}: {
  family: IpFamily;
  onChange: (family: IpFamily) => void;
  ipv4Price: string | null;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2 text-sm">
      <legend className="mb-1 font-medium">{t("publicIps.family")}</legend>
      {(["ipv4", "ipv6"] as const).map((f) => (
        <label
          key={f}
          className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2 hover:bg-surface-2"
        >
          <input
            type="radio"
            name="family"
            checked={family === f}
            onChange={() => onChange(f)}
            className="mt-0.5 size-4 accent-accent"
          />
          <span className="flex flex-col">
            <span className="font-medium">{f === "ipv4" ? "IPv4" : "IPv6"}</span>
            <span className="text-muted">
              {f === "ipv4"
                ? ipv4Price
                  ? t("publicIps.ipv4Price", { price: ipv4Price })
                  : t("publicIps.ipv4Paid")
                : t("publicIps.ipv6Free")}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function CreateDialog({
  projectId,
  open,
  onOpenChange,
  servers,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  servers: Server[];
}) {
  const { t, i18n } = useTranslation();
  const regions = useCatalogue(projectId, "regions").data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const create = useCreatePublicIp(projectId);
  const toast = useToast();
  const [name, setName] = useState("");
  const [family, setFamily] = useState<IpFamily>("ipv4");
  const [target, setTarget] = useState<"region" | "server">("region");
  const [region, setRegion] = useState("");
  const [serverId, setServerId] = useState("");
  const [autoDelete, setAutoDelete] = useState(false);
  const chosenRegion =
    target === "server"
      ? servers.find((s) => s.id === serverId)?.region
      : region || regions[0]?.code;
  const ipv4 = prices?.publicIpv4.find((p) => p.region === (chosenRegion ?? regions[0]?.code));
  const ipv4Price = ipv4 && prices ? money(ipv4.monthly.net, prices.currency, i18n.language) : null;
  const zones = [...new Set(regions.map((r) => r.zone))];

  const close = (next: boolean) => {
    if (!next) {
      setName("");
      setServerId("");
      create.reset();
    }
    onOpenChange(next);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        name: name.trim(),
        family,
        ...(target === "server"
          ? { serverId, autoDelete }
          : { region: region || regions[0]?.code || "" }),
      },
      {
        onSuccess: ({ publicIp }) => {
          toast({ tone: "ok", title: t("publicIps.created", { address: publicIp.address }) });
          close(false);
        },
      },
    );
  };
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("publicIps.create")}
      description={t("publicIps.createLead")}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="create-ip"
            busy={create.isPending}
            disabled={target === "server" && !serverId}
          >
            {t("publicIps.createSubmit")}
          </Button>
        </>
      }
    >
      <form id="create-ip" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("publicIps.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          hint={t("publicIps.nameHint")}
          error={fieldError(create.error, "name", t)}
        />
        <FamilyChoice family={family} onChange={setFamily} ipv4Price={ipv4Price} />
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="mb-1 font-medium">{t("publicIps.where")}</legend>
          <label className="flex min-h-6 items-center gap-2">
            <input
              type="radio"
              name="target"
              checked={target === "region"}
              onChange={() => setTarget("region")}
              className="size-4 accent-accent"
            />
            {t("publicIps.keepFree")}
          </label>
          <label className="flex min-h-6 items-center gap-2">
            <input
              type="radio"
              name="target"
              checked={target === "server"}
              onChange={() => setTarget("server")}
              className="size-4 accent-accent"
            />
            {t("publicIps.onServer")}
          </label>
        </fieldset>
        {target === "region" ? (
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium">{t("catalogue.region")}</span>
            <select
              value={region || regions[0]?.code || ""}
              onChange={(e) => setRegion(e.target.value)}
              className="min-h-10 rounded-lg border border-line bg-surface px-3 text-sm"
            >
              {zones.map((zone) => (
                <optgroup key={zone} label={t("publicIps.zone", { zone })}>
                  {regions
                    .filter((r) => r.zone === zone)
                    .map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.city} ({r.code})
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
            <span className="text-xs text-muted">{t("publicIps.regionHint")}</span>
          </label>
        ) : (
          <>
            <ServerSelect
              servers={servers}
              family={family}
              value={serverId}
              onChange={setServerId}
            />
            <label className="flex min-h-6 items-start gap-2 py-0.5 text-sm">
              <input
                type="checkbox"
                checked={autoDelete}
                onChange={(e) => setAutoDelete(e.target.checked)}
                className="mt-0.5 size-4 accent-accent"
              />
              <span>
                {t("publicIps.autoDeleteLabel")}
                <span className="block text-xs text-muted">{t("publicIps.autoDeleteHint")}</span>
              </span>
            </label>
          </>
        )}
        <Alert tone="neutral">{t("publicIps.billing")}</Alert>
        <ErrorMessage error={create.error} />
      </form>
    </Dialog>
  );
}

function AssignDialog({
  projectId,
  ip,
  servers,
  onClose,
}: {
  projectId: string;
  ip: PublicIp;
  servers: Server[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const assign = useAssignPublicIp(projectId);
  const toast = useToast();
  const [serverId, setServerId] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    assign.mutate(
      { id: ip.id, body: { serverId } },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("publicIps.assigning", { address: ip.address }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("publicIps.assignTitle", { address: ip.address })}
      description={t("publicIps.assignLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="assign-ip" busy={assign.isPending} disabled={!serverId}>
            {t("publicIps.assign")}
          </Button>
        </>
      }
    >
      <form id="assign-ip" onSubmit={submit} className="flex flex-col gap-4">
        <ServerSelect
          servers={servers}
          family={ip.family}
          region={ip.region}
          value={serverId}
          onChange={setServerId}
        />
        <ErrorMessage error={assign.error} />
      </form>
    </Dialog>
  );
}

/** What sets reverse DNS on an address: public IPs by default, floating IPs pass theirs. */
export interface ReverseDnsMutation {
  mutate(
    variables: { id: string; body: ReverseDnsRequest },
    options: { onSuccess: () => void },
  ): void;
  isPending: boolean;
  error: unknown;
}

export function ReverseDnsDialog({
  projectId,
  ip,
  onClose,
  mutation,
}: {
  projectId: string;
  ip: Pick<PublicIp, "id" | "family" | "address" | "reverseDns">;
  onClose: () => void;
  mutation?: ReverseDnsMutation;
}) {
  const { t } = useTranslation();
  const own = useReverseDns(projectId);
  const change = mutation ?? own;
  const toast = useToast();
  const v4 = ip.family === "ipv4";
  const [address, setAddress] = useState(v4 ? ip.address : ip.address.replace(/\/\d+$/, "1"));
  const current = ip.reverseDns.find((d) => d.ip === address)?.dnsPtr ?? "";
  const [name, setName] = useState(current);
  const invalid = name.trim() !== "" && !DNS_NAME_PATTERN.test(name.trim());
  const send = (dnsPtr: string | null) =>
    change.mutate(
      { id: ip.id, body: { ip: address.trim(), dnsPtr } },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("publicIps.dnsChanging", { address: address.trim() }) });
          onClose();
        },
      },
    );
  const submit = (event: FormEvent) => {
    event.preventDefault();
    send(name.trim());
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("publicIps.dnsTitle", { address: ip.address })}
      description={t("publicIps.dnsLead")}
      footer={
        <>
          <Button
            variant="ghost"
            icon={<RotateCcw className="size-4" aria-hidden />}
            onClick={() => send(null)}
            disabled={change.isPending}
          >
            {t("publicIps.dnsReset")}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="reverse-dns"
            busy={change.isPending}
            disabled={invalid || !name.trim()}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="reverse-dns" onSubmit={submit} className="flex flex-col gap-4">
        {!v4 && (
          <TextField
            label={t("publicIps.dnsAddress")}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            spellCheck={false}
            hint={t("publicIps.dnsAddressHint", { network: ip.address })}
            error={fieldError(change.error, "ip", t)}
          />
        )}
        <TextField
          label={t("publicIps.dnsName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          placeholder="mail.example.com"
          hint={t("publicIps.dnsNameHint")}
          info={<FieldHint text={t("publicIps.dnsWhy")} topic="public-ips" />}
          error={invalid ? t("publicIps.dnsInvalid") : undefined}
        />
        {!v4 && ip.reverseDns.length > 0 && (
          <ul className="flex flex-col gap-1 text-xs text-muted">
            {ip.reverseDns.map((d) => (
              <li key={d.ip} className="break-all font-mono">
                {d.ip} → {d.dnsPtr}
              </li>
            ))}
          </ul>
        )}
        {!fieldError(change.error, "ip", t) && <ErrorMessage error={change.error} />}
      </form>
    </Dialog>
  );
}

function RenameDialog({
  projectId,
  ip,
  onClose,
}: {
  projectId: string;
  ip: PublicIp;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePublicIp(projectId);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get("name") ?? "").trim();
    update.mutate({ id: ip.id, name }, { onSuccess: onClose });
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("publicIps.rename")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="rename-ip" busy={update.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="rename-ip" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("publicIps.name")}
          name="name"
          defaultValue={ip.name}
          required
          maxLength={128}
          error={fieldError(update.error, "name", t)}
        />
        <ErrorMessage error={update.error} />
      </form>
    </Dialog>
  );
}

type Open = { kind: "assign" | "dns" | "rename" | "delete"; ip: PublicIp } | null;

export function PublicIpsPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  // Guests may only look (docs/users.md): no create action; the API refuses them anyway.
  const canWrite = useCanWrite(projectId);
  const ips = usePublicIps(projectId);
  const servers = useServers(projectId).data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const unassign = useUnassignPublicIp(projectId);
  const protection = useIpProtection(projectId);
  const update = useUpdatePublicIp(projectId);
  const remove = useDeletePublicIp(projectId);
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);

  const failed = (error: unknown) =>
    toast({
      tone: "danger",
      title:
        error instanceof ApiProblem && error.problem
          ? problemText(t, error.problem)
          : t("common.unexpected"),
    });
  const serverOf = (ip: PublicIp) => servers.find((s) => s.id === ip.server?.id);
  const priceOf = (ip: PublicIp) => {
    if (ip.family === "ipv6") return t("publicIps.free");
    const p = prices?.publicIpv4.find((x) => x.region === ip.region);
    return p && prices ? money(p.monthly.net, prices.currency, i18n.language) : "—";
  };

  const createButton = (
    <Button
      onClick={() => setCreating(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("publicIps.create")}
    </Button>
  );

  const menu = (ip: PublicIp): MenuItem[][] => {
    const server = serverOf(ip);
    const running = server !== undefined && server.status !== "off";
    const assigned = ip.server !== null;
    return [
      [
        {
          label: t("publicIps.assign"),
          icon: <Link2 className="size-4" aria-hidden />,
          disabled: readOnly || assigned || ip.blocked,
          reason: readOnly
            ? readOnlyReason
            : ip.blocked
              ? t("publicIps.why.blocked")
              : t("publicIps.why.assigned", { server: ip.server?.name ?? "" }),
          onSelect: () => setOpen({ kind: "assign", ip }),
        },
        {
          label: t("publicIps.unassign"),
          icon: <Unlink className="size-4" aria-hidden />,
          disabled: readOnly || !assigned || running,
          reason: readOnly
            ? readOnlyReason
            : !assigned
              ? t("publicIps.why.notAssigned")
              : t("publicIps.why.serverRunning", { server: ip.server?.name ?? "" }),
          onSelect: () =>
            unassign.mutate(
              { id: ip.id },
              {
                onSuccess: () =>
                  toast({ tone: "ok", title: t("publicIps.unassigning", { address: ip.address }) }),
                onError: failed,
              },
            ),
        },
        {
          label: t("publicIps.dns"),
          icon: <Waypoints className="size-4" aria-hidden />,
          disabled: readOnly,
          reason: readOnlyReason,
          onSelect: () => setOpen({ kind: "dns", ip }),
        },
        {
          label: t("publicIps.rename"),
          icon: <Pencil className="size-4" aria-hidden />,
          disabled: readOnly,
          reason: readOnlyReason,
          onSelect: () => setOpen({ kind: "rename", ip }),
        },
      ],
      [
        {
          label: ip.autoDelete ? t("publicIps.autoDeleteOff") : t("publicIps.autoDeleteOn"),
          icon: <RotateCcw className="size-4" aria-hidden />,
          disabled: readOnly,
          reason: readOnlyReason,
          onSelect: () =>
            update.mutate(
              { id: ip.id, autoDelete: !ip.autoDelete },
              {
                onSuccess: () =>
                  toast({
                    tone: "ok",
                    title: ip.autoDelete
                      ? t("publicIps.autoDeleteOffDone", { address: ip.address })
                      : t("publicIps.autoDeleteOnDone", { address: ip.address }),
                  }),
                onError: failed,
              },
            ),
        },
        {
          label: ip.protection.delete ? t("publicIps.unprotect") : t("publicIps.protect"),
          icon: ip.protection.delete ? (
            <LockOpen className="size-4" aria-hidden />
          ) : (
            <Lock className="size-4" aria-hidden />
          ),
          disabled: readOnly,
          reason: readOnlyReason,
          onSelect: () =>
            protection.mutate(
              { id: ip.id, body: { delete: !ip.protection.delete } },
              { onError: failed },
            ),
        },
      ],
      [
        {
          label: t("publicIps.delete"),
          icon: <Trash2 className="size-4" aria-hidden />,
          danger: true,
          disabled: readOnly || ip.protection.delete || assigned,
          reason: readOnly
            ? readOnlyReason
            : ip.protection.delete
              ? t("publicIps.why.protected")
              : t("publicIps.why.deleteAssigned"),
          onSelect: () => setOpen({ kind: "delete", ip }),
        },
      ],
    ];
  };

  const deleting = open?.kind === "delete" ? open.ip : null;
  return (
    <>
      <PageHeader
        title={t("nav.ips")}
        intro={t("publicIps.intro")}
        help="public-ips"
        actions={canWrite && ips.data?.length ? createButton : undefined}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {ips.error ? (
        <ErrorMessage error={ips.error} />
      ) : (
        <DataTable<PublicIp>
          label={t("nav.ips")}
          rows={ips.data}
          rowKey={(ip) => ip.id}
          initialSort={{ key: "address", dir: "asc" }}
          muted={(ip) => ip.blocked}
          empty={
            <EmptyState
              icon={<Globe className="size-6" aria-hidden />}
              title={t("publicIps.empty")}
              action={canWrite ? createButton : undefined}
              help="public-ips"
            >
              {t("publicIps.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "address",
              header: t("publicIps.address"),
              primary: true,
              sort: (ip) => `${ip.family}-${ip.address}`,
              cell: (ip) => (
                <span className="flex min-w-0 flex-col">
                  <span className="whitespace-nowrap font-mono font-medium">{ip.address}</span>
                  <span className="truncate text-xs text-muted" title={ip.name}>
                    {ip.name}
                  </span>
                </span>
              ),
            },
            {
              key: "family",
              header: t("publicIps.family"),
              sort: (ip) => ip.family,
              cell: (ip) => <Badge>{ip.family === "ipv4" ? "IPv4" : "IPv6"}</Badge>,
            },
            {
              key: "region",
              header: t("catalogue.region"),
              sort: (ip) => ip.region,
              cell: (ip) => <span className="text-muted">{ip.region}</span>,
            },
            {
              key: "server",
              header: t("publicIps.server"),
              sort: (ip) => ip.server?.name ?? "",
              cell: (ip) =>
                ip.server ? (
                  <span className="break-all">
                    {ip.server.id ? (
                      <a
                        href={`/projects/${projectId}/servers/${ip.server.id}`}
                        className="text-accent hover:underline"
                      >
                        {ip.server.name}
                      </a>
                    ) : (
                      ip.server.name
                    )}
                    {serverOf(ip) && serverOf(ip)?.status !== "off" && (
                      <span className="block text-xs text-muted">{t("publicIps.serverOn")}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-muted">{t("publicIps.unassigned")}</span>
                ),
            },
            {
              key: "dns",
              header: (
                <span className="inline-flex items-center gap-1">
                  {t("publicIps.dnsShort")}
                  <FieldHint text={t("publicIps.dnsWhy")} topic="public-ips" />
                </span>
              ),
              cell: (ip) =>
                ip.reverseDns.length > 0 ? (
                  <span className="break-words text-xs">
                    {ip.reverseDns[0]?.dnsPtr}
                    {ip.reverseDns.length > 1 && ` +${ip.reverseDns.length - 1}`}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                ),
            },
            {
              key: "flags",
              header: (
                <span className="inline-flex items-center gap-1">
                  {t("publicIps.flags")}
                  <FieldHint text={t("publicIps.autoDeleteHint")} topic="public-ips" />
                </span>
              ),
              cell: (ip) => (
                <span className="flex flex-wrap gap-1">
                  {ip.autoDelete && <Badge tone="warn">{t("publicIps.autoDelete")}</Badge>}
                  {ip.protection.delete && <Badge tone="ok">{t("publicIps.protected")}</Badge>}
                  {ip.blocked && <Badge tone="danger">{t("publicIps.blocked")}</Badge>}
                  {!ip.autoDelete && !ip.protection.delete && !ip.blocked && (
                    <span className="text-muted">—</span>
                  )}
                </span>
              ),
            },
            {
              key: "price",
              header: t("publicIps.price"),
              align: "right",
              sort: (ip) => (ip.family === "ipv4" ? 1 : 0),
              cell: (ip) => <span className="whitespace-nowrap">{priceOf(ip)}</span>,
            },
            {
              key: "created",
              header: t("publicIps.createdAt"),
              hideOnPhone: true,
              sort: (ip) => ip.createdAt,
              cell: (ip) => (
                <span className="text-muted">{relativeTime(ip.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={menu}
        />
      )}
      <CreateDialog
        projectId={projectId}
        open={creating}
        onOpenChange={setCreating}
        servers={servers}
      />
      {open?.kind === "assign" && (
        <AssignDialog
          projectId={projectId}
          ip={open.ip}
          servers={servers}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === "dns" && (
        <ReverseDnsDialog projectId={projectId} ip={open.ip} onClose={() => setOpen(null)} />
      )}
      {open?.kind === "rename" && (
        <RenameDialog projectId={projectId} ip={open.ip} onClose={() => setOpen(null)} />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setOpen(null)}
        title={t("publicIps.deleteTitle", { address: deleting?.address ?? "" })}
        consequences={[
          t("publicIps.deleteLost"),
          t("publicIps.deleteDns"),
          deleting?.family === "ipv4" ? t("publicIps.deleteBilling") : t("publicIps.deleteFree"),
        ]}
        confirmText={deleting?.address ?? ""}
        actionLabel={t("publicIps.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("publicIps.deleted", { address: deleting.address }) });
              setOpen(null);
            },
            onError: failed,
          })
        }
      />
    </>
  );
}
